import { Fragment, useEffect, useRef, useState, type KeyboardEvent, type ReactNode, type RefObject } from 'react';
import { scrollEdges, type ScrollEdges } from '../table/tableModel';
import HelpTip from './HelpTip';

export interface TabItem<K extends string> {
  key: K;
  label: ReactNode;
  count?: number;
  icon?: ReactNode;
  /** A "?" right after the tab that explains what it shows (the existing HelpTip). */
  help?: { title: string; body: string };
}

interface TabsProps<K extends string> {
  items: TabItem<K>[];
  value: K;
  onChange: (key: K) => void;
  /** `underline` for page sections, `segmented` for 2–4 option view switches. */
  variant?: 'underline' | 'segmented';
  ariaLabel?: string;
  className?: string;
  /** Extra control(s) rendered inside the tablist after the tabs (e.g. a "more" menu tab). */
  trailing?: ReactNode;
}

/**
 * Which edges of the tab row hide tabs (it scrolls sideways when the tabs do
 * not fit, e.g. on a phone). Same measure as the DataTable scroll frame.
 */
function useScrollFade(ref: RefObject<HTMLDivElement>): ScrollEdges {
  const [edges, setEdges] = useState<ScrollEdges>({ overflow: false, start: false, end: false });
  useEffect(() => {
    const el = ref.current;
    if (!el) return undefined;
    const update = () => {
      const next = scrollEdges(el.scrollLeft, el.scrollWidth, el.clientWidth);
      setEdges((prev) => (prev.overflow === next.overflow && prev.start === next.start && prev.end === next.end ? prev : next));
    };
    update();
    el.addEventListener('scroll', update, { passive: true });
    const ro = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(update) : null;
    ro?.observe(el);
    // FQ-042ט: the row's own box does not change when its tabs do (a count badge arrives, a
    // web font loads), so watch the tabs too or the cut-off fade is never switched on.
    Array.from(el.children).forEach((c) => ro?.observe(c));
    const mo = typeof MutationObserver !== 'undefined' ? new MutationObserver(update) : null;
    mo?.observe(el, { childList: true, subtree: true, characterData: true });
    void document.fonts?.ready?.then(update);
    return () => {
      mo?.disconnect();
      el.removeEventListener('scroll', update);
      ro?.disconnect();
    };
  }, [ref]);
  return edges;
}

/**
 * Accessible tabs (role=tablist, arrow keys, Home/End) in the two sanctioned
 * styles (DESIGN_SYSTEM.md §6). Arrow direction follows the document direction.
 * When the row is cut off it scrolls sideways, and the cut edge fades
 * (`.scroll-fade`, index.css) so it reads as "more this way".
 */
export default function Tabs<K extends string>({ items, value, onChange, variant = 'underline', ariaLabel, className = '', trailing }: TabsProps<K>) {
  const refs = useRef<(HTMLButtonElement | null)[]>([]);
  const listRef = useRef<HTMLDivElement>(null);
  const edges = useScrollFade(listRef);
  const onKey = (e: KeyboardEvent<HTMLButtonElement>, i: number) => {
    const rtl = document.documentElement.dir === 'rtl';
    const next = rtl ? 'ArrowLeft' : 'ArrowRight';
    const prev = rtl ? 'ArrowRight' : 'ArrowLeft';
    let j = -1;
    if (e.key === next) j = (i + 1) % items.length;
    else if (e.key === prev) j = (i - 1 + items.length) % items.length;
    else if (e.key === 'Home') j = 0;
    else if (e.key === 'End') j = items.length - 1;
    if (j < 0) return;
    e.preventDefault();
    refs.current[j]?.focus();
    refs.current[j]?.scrollIntoView({ block: 'nearest', inline: 'nearest' });
    onChange(items[j].key);
  };

  const segmented = variant === 'segmented';
  return (
    <div
      ref={listRef}
      role="tablist"
      aria-label={ariaLabel}
      data-fade-start={edges.start ? '' : undefined}
      data-fade-end={edges.end ? '' : undefined}
      className={`${segmented ? 'tabs-pill' : 'tabs'} scroll-fade ${className}`}
    >
      {items.map((it, i) => {
        const active = it.key === value;
        return (
          <Fragment key={it.key}>
          <button
            ref={(el) => { refs.current[i] = el; }}
            type="button"
            role="tab"
            aria-selected={active}
            tabIndex={active ? 0 : -1}
            onClick={() => onChange(it.key)}
            onKeyDown={(e) => onKey(e, i)}
            className={`${segmented ? (active ? 'tab-pill-active' : 'tab-pill-inactive') : (active ? 'tab-active' : 'tab-inactive')} inline-flex items-center gap-1.5`}
          >
            {it.icon}
            {it.label}
            {typeof it.count === 'number' && (
              <span className={`text-xs tabular-nums ${active ? 'text-surface-700' : 'text-surface-500'}`}>{it.count}</span>
            )}
          </button>
          {it.help && <HelpTip title={it.help.title} body={it.help.body} className="me-1" />}
          </Fragment>
        );
      })}
      {trailing}
    </div>
  );
}
